import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import NotificationBell from './NotificationBell';
import { 
  LayoutDashboard, 
  Truck, 
  MapPin, 
  CalendarDays, 
  Settings,
  Menu,
  X,
  ChevronLeft,
  BarChart3,
  Users,
  ScrollText,
  Trophy,
  FileText,
  Inbox,
  Shield,
  Database,
  HardDrive
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PERMISSION_MAP = {
  '/admin': 'dashboard',
  '/admin/bookings': 'bookings',
  '/admin/calendar': 'calendar',
  '/admin/customers': 'customers',
  '/admin/trailers': 'trailers',
  '/admin/locations': 'locations',
  '/admin/analytics': 'analytics',
  '/admin/audit-log': 'audit_log',
  '/admin/settings': 'settings',
  '/admin/blog': 'blog',
  '/admin/inquiries': 'inquiries',
  '/admin/data-management': 'settings',
  '/admin/legal': 'settings',
  '/admin/backups': 'settings',
  '/admin/roles': 'role_management',
};

const AdminLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const { token, isAdmin } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [permissions, setPermissions] = useState(null);

  useEffect(() => {
    if (token) fetchPermissions();
  }, [token]);

  const fetchPermissions = async () => {
    try {
      const res = await fetch(`${API_URL}/api/admin/my-permissions`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setPermissions(data.permissions);
      }
    } catch (e) {
      console.error('Error fetching permissions:', e);
    }
  };

  const allMenuItems = [
    { path: '/admin', icon: LayoutDashboard, label: t('admin.dashboard'), perm: 'dashboard' },
    { path: '/admin/bookings', icon: CalendarDays, label: t('admin.bookings'), perm: 'bookings' },
    { path: '/admin/calendar', icon: CalendarDays, label: 'Kalender', perm: 'calendar' },
    { path: '/admin/customers', icon: Users, label: 'Kunden', perm: 'customers' },
    { path: '/admin/trailers', icon: Truck, label: t('admin.trailers'), perm: 'trailers' },
    { path: '/admin/locations', icon: MapPin, label: t('admin.locations'), perm: 'locations' },
    { path: '/admin/analytics', icon: BarChart3, label: 'Analytics', perm: 'analytics' },
    { path: '/admin/audit-log', icon: ScrollText, label: 'Audit-Log', perm: 'audit_log' },
    { path: '/admin/settings', icon: Settings, label: t('admin.settings'), perm: 'settings' },
    { path: '/admin/roles', icon: Shield, label: 'Rollen & Rechte', perm: 'role_management' },
    { path: '/admin/blog', icon: FileText, label: 'Blog / SEO', perm: 'blog' },
    { path: '/admin/inquiries', icon: Inbox, label: 'Anfragen & Offerten', perm: 'inquiries' },
    { path: '/admin/data-management', icon: Database, label: 'Daten-Management', perm: 'settings' },
    { path: '/admin/legal', icon: FileText, label: 'Rechtliches', perm: 'settings' },
    { path: '/admin/backups', icon: HardDrive, label: 'Backups', perm: 'settings' },
  ];

  const menuItems = isAdmin
    ? allMenuItems
    : allMenuItems.filter(item => permissions?.[item.perm]);

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  const Sidebar = ({ mobile = false }) => (
    <div className={`${mobile ? 'w-full' : 'w-64'} bg-white border-r border-[hsl(var(--border))] h-full flex flex-col`}>
      {/* Logo */}
      <div className="p-4 border-b border-[hsl(var(--border))]">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-10 h-10 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center">
            <MapPin className="w-6 h-6 text-white" />
          </div>
          <span className="font-semibold">ZO Admin</span>
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 p-4">
        <ul className="space-y-1">
          {menuItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <li key={item.path}>
                <Link
                  to={item.path}
                  onClick={() => mobile && setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-[hsl(var(--primary))] text-white'
                      : 'text-[hsl(var(--muted-foreground))] hover:bg-[hsl(var(--muted))] hover:text-[hsl(var(--foreground))]'
                  }`}
                  data-testid={`admin-nav-${item.path.split('/').pop() || 'dashboard'}`}
                >
                  <Icon className="w-5 h-5" />
                  <span>{item.label}</span>
                </Link>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* Back to Site */}
      <div className="p-4 border-t border-[hsl(var(--border))]">
        <Link to="/" className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] transition-colors">
          <ChevronLeft className="w-4 h-4" />
          <span>Zurück zur Webseite</span>
        </Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[hsl(var(--surface-2))]">
      {/* Desktop Sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64">
        <Sidebar />
      </div>

      {/* Mobile Header */}
      <div className="lg:hidden sticky top-0 z-50 bg-white border-b border-[hsl(var(--border))]">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/admin" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-[hsl(var(--primary))] rounded-lg flex items-center justify-center">
              <MapPin className="w-5 h-5 text-white" />
            </div>
            <span className="font-semibold">ZO Admin</span>
          </Link>
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2"
            data-testid="admin-mobile-menu"
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div className="absolute inset-0 bg-black/50" onClick={() => setSidebarOpen(false)} />
          <div className="absolute left-0 top-0 h-full w-64">
            <Sidebar mobile />
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Bar */}
        <header className="hidden lg:flex items-center justify-between h-16 px-8 bg-white border-b border-[hsl(var(--border))]">
          <h1 className="text-xl font-semibold">
            {menuItems.find(item => item.path === location.pathname)?.label || 'Admin'}
          </h1>
          <div className="flex items-center gap-4">
            <NotificationBell />
            <div className="lang-switch">
              {['de', 'en', 'fr', 'it'].map((lng) => (
                <button
                  key={lng}
                  onClick={() => changeLanguage(lng)}
                  className={`lang-btn ${i18n.language === lng ? 'active' : ''}`}
                >
                  {lng}
                </button>
              ))}
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          {children}
        </main>

        {/* Admin Footer with InvictaX attribution */}
        <footer className="mt-auto px-4 lg:px-8 py-4 border-t border-[hsl(var(--border))] text-xs text-[hsl(var(--muted-foreground))] text-center">
          Powered by{' '}
          <a
            href="https://www.invictaflow.ch"
            target="_blank"
            rel="noopener noreferrer"
            className="hover:text-[hsl(var(--foreground))] underline underline-offset-2"
            data-testid="admin-footer-invictax-link"
          >
            InvictaX Group GmbH
          </a>
        </footer>
      </div>
    </div>
  );
};

export default AdminLayout;
